import { useState, useEffect, useCallback } from "react";
import { AlertTriangle, CheckCircle, XCircle, Plus, ThumbsUp, Clock, Loader } from "lucide-react";
import API from "../api";
import { useAuth } from "../contexts/AuthContext";

const TYPES = ["all","road","trail","temple","weather","other"];
const STATUS_CONFIG = {
  open:    { icon: CheckCircle,    color: "text-green-400",  bg: "bg-green-400/10",  label: "Open" },
  closed:  { icon: XCircle,       color: "text-red-400",    bg: "bg-red-400/10",    label: "Closed" },
  caution: { icon: AlertTriangle, color: "text-yellow-400", bg: "bg-yellow-400/10", label: "Caution" },
};

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return "just now";
  if (diff < 3600)  return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return `${Math.floor(diff/86400)}d ago`;
}

const SAMPLE_CONDITIONS = [
  { id: "s1", place_name: "Badrinath Highway (NH-7)", type: "road", status: "open", note: "Clear weather, traffic flowing smoothly near Joshimath.", reported_at: new Date(Date.now() - 3600000).toISOString(), reporter_name: "Ravi M.", upvotes: 24 },
  { id: "s2", place_name: "Kedarnath Trek Route", type: "trail", status: "caution", note: "Slippery near Linchauli due to recent rain. Wear good grip shoes.", reported_at: new Date(Date.now() - 7200000).toISOString(), reporter_name: "Priya S.", upvotes: 15 },
  { id: "s3", place_name: "Tungnath Temple", type: "temple", status: "open", note: "Temple portals are open. Moderate crowd today.", reported_at: new Date(Date.now() - 14400000).toISOString(), reporter_name: "Amit U.", upvotes: 42 },
  { id: "s4", place_name: "Nainital Lake Area", type: "weather", status: "open", note: "Pleasant 18°C, slight drizzle expected in the evening.", reported_at: new Date(Date.now() - 1800000).toISOString(), reporter_name: "Neha T.", upvotes: 8 },
  { id: "s5", place_name: "Rishikesh-Devprayag", type: "road", status: "caution", note: "Minor landslide debris cleared, single lane open near Byasi.", reported_at: new Date(Date.now() - 86400000).toISOString(), reporter_name: "Vikram K.", upvotes: 11 },
];

const CITIES_LOOKUP = {
  "rishikesh": { lat: 30.0869, lon: 78.2676 },
  "haridwar": { lat: 29.9457, lon: 78.1642 },
  "dehradun": { lat: 30.3165, lon: 78.0322 },
  "mussoorie": { lat: 30.4598, lon: 78.0644 },
  "nainital": { lat: 29.3803, lon: 79.4636 },
  "kedarnath": { lat: 30.7352, lon: 79.0669 },
  "badrinath": { lat: 30.7433, lon: 79.4938 },
  "almora": { lat: 29.5892, lon: 79.6467 },
  "joshimath": { lat: 30.5506, lon: 79.5660 },
  "auli": { lat: 30.5330, lon: 79.5695 },
  "chopta": { lat: 30.4851, lon: 79.2001 },
};

export default function Conditions() {
  const { user }                      = useAuth();
  const [conditions, setConditions]   = useState([]);
  const [weather, setWeather]         = useState([]);
  const [advisory, setAdvisory]       = useState(null);
  const [zones, setZones]             = useState([]);
  const [calendar, setCalendar]       = useState([]);
  const [filter, setFilter]           = useState("all");
  const [loading, setLoading]         = useState(true);
  const [showForm, setShowForm]       = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [form, setForm]               = useState({
    place_name: "", type: "road", status: "open", note: ""
  });

  const [weatherSearch, setWeatherSearch] = useState("");
  const [weatherResult, setWeatherResult] = useState(null);
  const [weatherSearching, setWeatherSearching] = useState(false);
  const [weatherSearchError, setWeatherSearchError] = useState("");

  const handleWeatherSearch = async (e) => {
    e.preventDefault();
    if (!weatherSearch.trim()) return;
    const q = weatherSearch.trim().toLowerCase();
    const coords = CITIES_LOOKUP[q];
    if (!coords) {
      setWeatherSearchError("City not found in lookup. Try Rishikesh, Dehradun, Nainital, Kedarnath, etc.");
      setWeatherResult(null);
      return;
    }
    
    setWeatherSearching(true);
    setWeatherSearchError("");
    try {
      const res = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${coords.lat}&longitude=${coords.lon}&current=temperature_2m,weathercode,windspeed_10m&timezone=Asia/Kolkata`);
      const data = await res.json();
      setWeatherResult({
        name: q.charAt(0).toUpperCase() + q.slice(1),
        temp: data.current.temperature_2m,
        wind: data.current.windspeed_10m,
      });
    } catch (err) {
      setWeatherSearchError("Failed to fetch weather.");
    } finally {
      setWeatherSearching(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { type: filter } : {};
      const res = await API.get("/conditions", { params });
      setConditions(res.data.conditions || []);
    } catch (err) { console.error("Failed to load conditions", err); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    API.get("/conditions/weather")
      .then(r => setWeather(r.data.weather || []))
      .catch(e => console.error("weather error", e));
    API.get("/conditions/current-advisory").then(r => setAdvisory(r.data)).catch(e => console.error(e));
    API.get("/conditions/zone-status").then(r => setZones(r.data.zones)).catch(e => console.error(e));
    API.get("/conditions/seasonal-calendar").then(r => setCalendar(r.data.calendar)).catch(e => console.error(e));
  }, []);

  const submit = async () => {
    if (!form.place_name.trim()) return;
    setSubmitting(true);
    try {
      const res = await API.post("/conditions", form);
      setConditions(prev => [res.data, ...prev]);
      setShowForm(false);
      setForm({ place_name: "", type: "road", status: "open", note: "" });
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  const upvote = async (id) => {
    try {
      await API.post(`/conditions/${id}/upvote`);
      setConditions(prev => prev.map(c =>
        c.id === id ? { ...c, upvotes: c.upvotes + 1 } : c
      ));
    } catch {}
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f8' }}>
      <style>{`
        @keyframes conditionPulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        .pulse-dot {
          width: 8px;
          height: 8px;
          background: #e07b38;
          border-radius: 50%;
          display: inline-block;
          animation: conditionPulse 1.5s ease-in-out infinite;
          margin-right: 6px;
        }
      `}</style>

      {/* Hero / Header */}
      <div style={{ background: '#1a2a4a', padding: '40px 20px 32px' }}>
        <div style={{ maxWidth: '640px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
            <div>
              <h1 style={{ color: 'white', fontSize: '26px', fontWeight: 700, margin: '0 0 6px' }}>Live Conditions</h1>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>Roads, trails &amp; temples — reported by real travelers</p>
            </div>
            {user && (
              <button onClick={() => setShowForm(s => !s)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#e07b38', color: 'white', fontWeight: 600, fontSize: '13px', padding: '8px 16px', borderRadius: '999px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <Plus size={16} /> Report
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '24px 16px 80px' }}>

        {/* live weather strip */}
        {weather.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '12px', fontWeight: 600, color: '#e07b38', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
              <span className="pulse-dot"></span>
              🌤️ Live Weather · Auto Updated
            </p>
            <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px' }}>
              {weather.map(w => (
                <div key={w.city} style={{ background: '#f0f2f8', border: '1px solid rgba(26,42,74,0.1)', borderRadius: '12px', padding: '12px', flexShrink: 0, minWidth: '120px' }}>
                  <p style={{ fontWeight: 500, color: '#1a2a4a', fontSize: '14px', margin: '0 0 4px' }}>{w.city}</p>
                  <p style={{ fontSize: '24px', fontWeight: 700, color: '#1a2a4a', margin: '0 0 4px' }}>{w.temp}°C</p>
                  <p style={{ color: '#6b7280', fontSize: '12px', margin: '0 0 4px' }}>{w.description}</p>
                  <p style={{ color: '#6b7280', fontSize: '12px', margin: 0 }}>💧{w.humidity}% 💨{w.wind_kph}km/h</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Check Weather for Any Place */}
        <div style={{ marginBottom: '24px', background: 'white', border: '1px solid rgba(0,0,0,0.06)', borderRadius: '12px', padding: '20px' }}>
          <h3 style={{ fontWeight: 700, color: '#1a2a4a', marginBottom: '16px', margin: '0 0 16px' }}>🌦️ Check Weather for Any Place</h3>
          <form onSubmit={handleWeatherSearch} style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              value={weatherSearch}
              onChange={e => setWeatherSearch(e.target.value)}
              placeholder="Search any place in Uttarakhand..."
              style={{ flex: 1, background: '#f0f2f8', border: '1px solid rgba(26,42,74,0.1)', borderRadius: '8px', padding: '8px 14px', fontSize: '14px', color: '#1a2a4a', outline: 'none' }}
            />
            <button type="submit" disabled={weatherSearching} style={{ background: '#1a2a4a', color: 'white', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>
              {weatherSearching ? "Searching..." : "Search"}
            </button>
          </form>
          {weatherSearchError && <p style={{ color: '#dc2626', fontSize: '13px', marginTop: '8px' }}>{weatherSearchError}</p>}
          {weatherResult && (
            <div style={{ marginTop: '16px', padding: '16px', background: '#f0f2f8', border: '1px solid rgba(26,42,74,0.1)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '16px', fontWeight: 700, color: '#1a2a4a', margin: '0 0 4px' }}>{weatherResult.name}</p>
                <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>Wind: {weatherResult.wind} km/h</p>
              </div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#1a2a4a' }}>{weatherResult.temp}°C</div>
            </div>
          )}
        </div>

        {/* Is Now A Good Time Banner */}
        {advisory && (
          <div style={{ marginBottom: '24px', background: '#fff3e0', border: 'none', borderLeft: '3px solid #e07b38', borderRadius: '12px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <h2 style={{ color: '#7a3a00', fontWeight: 700, fontSize: '16px', marginBottom: '8px', margin: '0 0 8px' }}>{advisory.month} — Travelling Now?</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '13px', fontWeight: 500 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckCircle size={14} style={{ color: '#16a34a' }}/>
                  <span style={{ color: '#7a3a00' }}>GOOD FOR: </span>
                  <span style={{ background: '#e8f5e9', color: '#0a5a2a', borderRadius: '999px', padding: '4px 10px', fontSize: '12px' }}>{advisory.good_for}</span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <AlertTriangle size={14} style={{ color: '#e07b38' }}/>
                  <span style={{ color: '#7a3a00' }}>AVOID: </span>
                  <span style={{ background: '#fee2e2', color: '#7f1d1d', borderRadius: '999px', padding: '4px 10px', fontSize: '12px' }}>{advisory.avoid}</span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Clock size={14} style={{ color: '#1a2a4a' }}/>
                  <span style={{ color: '#7a3a00' }}>PLAN BEFORE: </span>
                  <span style={{ color: '#7a3a00' }}>{advisory.plan_before}</span>
                </span>
              </div>
            </div>
            <div style={{ background: '#fee2e2', color: '#7f1d1d', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '8px', alignSelf: 'flex-start' }}>
              🚨 ROAD ALERT: {advisory.road_alert}
            </div>
          </div>
        )}

        {/* Zone-wise Status */}
        {zones.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#1a2a4a', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>📍 Zone-wise Current Status</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {zones.map((z, i) => (
                <div key={i} style={{ background: 'white', border: '1px solid rgba(0,0,0,0.06)', borderRadius: '12px', padding: '16px' }}>
                  <h3 style={{ fontWeight: 700, color: '#1a2a4a', fontSize: '14px', marginBottom: '4px', margin: '0 0 4px' }}>{z.name}</h3>
                  <p style={{ color: '#6b7280', fontSize: '12px', marginBottom: '12px', margin: '0 0 12px' }}>Elev: {z.elevation}</p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ color: '#1a2a4a', fontWeight: 700, fontSize: '13px' }}>{z.status}</span>
                    {z.temp && <span style={{ fontSize: '13px', fontWeight: 600, background: 'rgba(26,42,74,0.05)', padding: '4px 8px', borderRadius: '6px', color: '#1a2a4a' }}>{z.temp}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Road Conditions Section */}
        <div style={{ marginBottom: '24px', background: 'white', border: '1px solid rgba(0,0,0,0.06)', borderRadius: '12px', padding: '20px' }}>
          <h3 style={{ fontWeight: 700, color: '#1a2a4a', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 16px' }}>
            <AlertTriangle size={18} style={{ color: '#e07b38' }}/> Key Route Status
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px', paddingBottom: '12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <span style={{ fontWeight: 500, color: '#1a2a4a' }}>NH7 Haridwar-Rishikesh</span>
              <span style={{ color: '#16a34a', fontWeight: 700, background: 'rgba(22,163,74,0.1)', padding: '4px 10px', borderRadius: '6px', fontSize: '12px' }}>Good</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px', paddingBottom: '12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <span style={{ fontWeight: 500, color: '#1a2a4a' }}>Rishikesh-Chopta</span>
              <span style={{ color: '#d97706', fontWeight: 700, background: 'rgba(217,119,6,0.1)', padding: '4px 10px', borderRadius: '6px', fontSize: '12px' }}>Caution</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px', paddingBottom: '12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <span style={{ fontWeight: 500, color: '#1a2a4a' }}>Joshimath-Badrinath</span>
              <span style={{ color: '#dc2626', fontWeight: 700, background: 'rgba(220,38,38,0.1)', padding: '4px 10px', borderRadius: '6px', fontSize: '12px' }}>Closed (Night)</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' }}>
              <span style={{ fontWeight: 500, color: '#1a2a4a' }}>Haldwani-Nainital</span>
              <span style={{ color: '#16a34a', fontWeight: 700, background: 'rgba(22,163,74,0.1)', padding: '4px 10px', borderRadius: '6px', fontSize: '12px' }}>Good</span>
            </div>
          </div>
        </div>

        {/* Seasonal Travel Calendar */}
        {calendar.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#1a2a4a', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>📅 Seasonal Travel Calendar</p>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {calendar.map((m, i) => {
                let bgStyle = { background: 'white', borderColor: 'rgba(0,0,0,0.06)', color: '#1a2a4a' };
                if (m.month === "May") bgStyle = { background: 'rgba(26,42,74,0.12)', borderColor: '#1a2a4a', color: '#1a2a4a', fontWeight: 700 };
                else if (m.season === "Monsoon") bgStyle = { background: 'rgba(59,130,246,0.1)', borderColor: 'rgba(59,130,246,0.2)', color: '#1d4ed8' };
                else if (m.season === "Winter") bgStyle = { background: 'rgba(6,182,212,0.1)', borderColor: 'rgba(6,182,212,0.2)', color: '#0e7490' };
                return (
                  <div key={i} style={{ borderRadius: '12px', padding: '12px', textAlign: 'center', border: '1px solid', display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '80px', transition: 'transform 0.2s', ...bgStyle }}>
                    <span style={{ fontWeight: 700, fontSize: '13px' }}>{m.month}</span>
                    <span style={{ fontSize: '10px', lineHeight: 1.3, marginTop: '4px', opacity: 0.8 }}>{m.desc}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Packing Recommendations */}
        <div style={{ marginBottom: '24px', background: 'white', border: '1px solid rgba(0,0,0,0.06)', borderRadius: '12px', padding: '20px', position: 'relative', overflow: 'hidden' }}>
          <h3 style={{ fontWeight: 700, color: '#1a2a4a', marginBottom: '16px', margin: '0 0 16px', position: 'relative', zIndex: 1 }}>🎒 Packing for May</h3>
          <ul style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px', color: '#6b7280', listStyle: 'none', padding: 0, margin: 0, position: 'relative', zIndex: 1 }}>
            <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><CheckCircle size={16} style={{ color: '#16a34a', flexShrink: 0 }}/> <span><strong style={{ color: '#1a2a4a' }}>Light cottons</strong> for day (low altitudes)</span></li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><CheckCircle size={16} style={{ color: '#16a34a', flexShrink: 0 }}/> <span><strong style={{ color: '#1a2a4a' }}>Light jacket/fleece</strong> for evening</span></li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><CheckCircle size={16} style={{ color: '#16a34a', flexShrink: 0 }}/> <span><strong style={{ color: '#1a2a4a' }}>Sunscreen &amp; sunglasses</strong></span></li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><CheckCircle size={16} style={{ color: '#16a34a', flexShrink: 0 }}/> <span><strong style={{ color: '#1a2a4a' }}>Sturdy trekking shoes</strong></span></li>
          </ul>
        </div>

        {/* report form */}
        {showForm && (
          <div style={{ background: 'white', border: '1px solid rgba(26,42,74,0.2)', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
            <h3 style={{ fontWeight: 600, color: '#1a2a4a', marginBottom: '16px', margin: '0 0 16px' }}>Report a condition</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input
                value={form.place_name}
                onChange={e => setForm(f => ({ ...f, place_name: e.target.value }))}
                placeholder="Place name (e.g. Kedarnath road, Chopta trail)"
                style={{ width: '100%', background: '#f0f2f8', border: '1px solid rgba(26,42,74,0.1)', borderRadius: '8px', padding: '10px 14px', fontSize: '14px', color: '#1a2a4a', outline: 'none', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: '12px' }}>
                <select value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                  style={{ flex: 1, background: '#f0f2f8', border: '1px solid rgba(26,42,74,0.1)', color: '#1a2a4a', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}>
                  {TYPES.filter(t => t !== "all").map(t => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
                <select value={form.status}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  style={{ flex: 1, background: '#f0f2f8', border: '1px solid rgba(26,42,74,0.1)', color: '#1a2a4a', borderRadius: '8px', padding: '10px 12px', fontSize: '14px' }}>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="caution">Caution</option>
                </select>
              </div>
              <textarea
                value={form.note}
                onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                placeholder="Optional: add details (e.g. snow above 3000m, road blocked near km 42)"
                rows={2}
                style={{ width: '100%', background: '#f0f2f8', border: '1px solid rgba(26,42,74,0.1)', borderRadius: '8px', padding: '10px 14px', fontSize: '14px', color: '#1a2a4a', outline: 'none', resize: 'none', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => setShowForm(false)}
                  style={{ flex: 1, border: '1px solid rgba(26,42,74,0.15)', color: '#1a2a4a', padding: '10px', borderRadius: '8px', fontSize: '14px', background: 'transparent', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={submit} disabled={submitting || !form.place_name.trim()}
                  style={{ flex: 1, background: '#1a2a4a', color: 'white', fontWeight: 600, padding: '10px', borderRadius: '8px', fontSize: '14px', border: 'none', cursor: 'pointer', opacity: submitting || !form.place_name.trim() ? 0.4 : 1 }}>
                  {submitting ? "Submitting..." : "Submit Report"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* type filter */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {TYPES.map(t => (
            <button key={t} onClick={() => setFilter(t)}
              style={{
                padding: '8px 16px',
                borderRadius: '999px',
                fontSize: '13px',
                fontWeight: 500,
                textTransform: 'capitalize',
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: filter === t ? '#1a2a4a' : 'white',
                color: filter === t ? 'white' : '#6b7280',
                border: filter === t ? 'none' : '1px solid rgba(0,0,0,0.12)',
              }}>
              {t}
            </button>
          ))}
        </div>

        {/* conditions list */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
            <Loader size={24} style={{ color: '#e07b38', animation: 'spin 1s linear infinite' }} />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {(conditions.length > 0 ? conditions : (filter === "all" ? SAMPLE_CONDITIONS : SAMPLE_CONDITIONS.filter(c => c.type === filter))).map(c => {
              const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.open;
              const Icon = cfg.icon;
              return (
                <div key={c.id} style={{ background: 'white', border: '1px solid rgba(0,0,0,0.06)', borderRadius: '12px', padding: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1, minWidth: 0 }}>
                      <div className={`w-9 h-9 rounded-full ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <Icon size={16} className={cfg.color} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <p style={{ fontWeight: 500, color: '#1a2a4a', margin: 0 }}>{c.place_name}</p>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                          <span style={{ fontSize: '12px', color: '#6b7280', textTransform: 'capitalize', background: 'rgba(26,42,74,0.05)', padding: '2px 8px', borderRadius: '999px' }}>{c.type}</span>
                        </div>
                        {c.note && <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '4px', margin: '4px 0 0' }}>{c.note}</p>}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                          <span style={{ color: '#9ca3af', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Clock size={11} /> {timeAgo(c.reported_at)}
                          </span>
                          <span style={{ color: '#9ca3af', fontSize: '12px' }}>by {c.reporter_name}</span>
                        </div>
                      </div>
                    </div>
                    <button onClick={() => upvote(c.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9ca3af', border: 'none', background: 'transparent', cursor: 'pointer', flexShrink: 0, fontSize: '14px' }}>
                      <ThumbsUp size={14} />
                      <span>{c.upvotes || 0}</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
